/**
 * Remembers finished lunch orders on this device so a parent can repeat one.
 *
 * Why the browser and not the customer record: this shop runs Shopify's new customer accounts,
 * where the storefront's Liquid `customer` object comes and goes — the same session rendered
 * "Account" one minute and "Log in" the next — so past orders cannot be read server-side with
 * any confidence. Shopify's own "Buy again" is no help either: it re-adds the variants with
 * **every line item property stripped** and the per-day lines collapsed into a quantity, so the
 * cart it produces cannot say which bread, which removals, or which student. The only place the
 * full configuration reliably survives is the device that placed it.
 *
 * The trade is explicit: a new phone, a different browser, or cleared site data starts empty.
 * Nothing here is required for ordering — it only ever pre-fills a form the customer can edit.
 *
 * Storage:
 *   localStorage   burton:orders   the last few finished orders, newest first
 *   sessionStorage burton:repeat   one order handed to the funnel for the next page load
 */
(function () {
  const ORDERS_KEY = 'burton:orders';
  const SEED_KEY = 'burton:repeat';
  const LIMIT = 8;

  /*
   * Every read and write is wrapped: Safari's private mode throws on setItem, and a half-written
   * or hand-edited value would otherwise take the whole funnel down on load. Remembering orders
   * is a convenience, so it fails to "no history" rather than to a broken page.
   */
  function read(store, key) {
    try {
      return JSON.parse(store.getItem(key));
    } catch (error) {
      return null;
    }
  }

  function write(store, key, value) {
    try {
      store.setItem(key, JSON.stringify(value));
      return true;
    } catch (error) {
      return false;
    }
  }

  /** Every variant this order buys, sandwich first — the shape a cart can be compared against. */
  function variantsOf(order) {
    const ids = [order?.product?.variantId];
    (order?.addons || []).forEach((addon) => ids.push(addon.variantId));
    return ids.filter(Boolean).map(String);
  }

  /*
   * Two orders count as the same when they buy the same things, the same way, for the same
   * student — the delivery days are deliberately excluded, because a parent repeating last
   * week's lunch on new days should update the existing entry rather than add a near-duplicate.
   */
  function fingerprint(order) {
    return JSON.stringify([
      variantsOf(order).sort(),
      order?.build?.bread || '',
      (order?.build?.breadOptions || []).slice().sort(),
      (order?.build?.removed || []).slice().sort(),
      order?.student || '',
      order?.quantity || 1,
    ]);
  }

  const OrderMemory = {
    /** Remembered orders, newest first. Always an array, even when storage is unavailable. */
    list() {
      const rows = read(localStorage, ORDERS_KEY);
      return Array.isArray(rows) ? rows.filter((row) => row && row.product) : [];
    },

    /** Records a finished order, replacing an identical earlier one rather than stacking it. */
    save(order) {
      if (!order || !order.product) return;
      const mark = fingerprint(order);
      const rows = this.list().filter((row) => fingerprint(row) !== mark);
      rows.unshift(order);
      write(localStorage, ORDERS_KEY, rows.slice(0, LIMIT));
    },

    forget(id) {
      write(localStorage, ORDERS_KEY, this.list().filter((row) => row.id !== id));
    },

    /**
     * The remembered order that buys exactly what these variant ids buy. Used on the cart page
     * to put the options back onto a "Buy again" cart, which arrives carrying none of them.
     * Exact match on the set, so a cart holding a sandwich and a cookie never resurrects an
     * order that also had chips.
     */
    matching(variantIds) {
      const wanted = Array.from(new Set(variantIds.map(String))).sort().join('|');
      return this.list().find((row) => {
        const have = Array.from(new Set(variantsOf(row))).sort().join('|');
        return have === wanted;
      }) || null;
    },

    variantsOf,

    /** Hands one order to the funnel for the next page load; read once, then dropped. */
    putSeed(order) {
      return write(sessionStorage, SEED_KEY, order);
    },

    takeSeed() {
      const seed = read(sessionStorage, SEED_KEY);
      try {
        sessionStorage.removeItem(SEED_KEY);
      } catch (error) {
        /* nothing to clean up */
      }
      return seed && seed.product ? seed : null;
    },

    /** "Sandwich, side and dessert" — the same phrasing the funnel's recap uses. */
    describe(order) {
      const parts = [order?.product?.label].concat((order?.addons || []).map((addon) => addon.label));
      const items = parts.filter(Boolean);
      if (items.length <= 1) return items.join('');
      return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
    },
  };

  window.OrderMemory = OrderMemory;
})();
